// frontend/components/grammarAssistantPanel/GrammarAssistantPanel.jsx
import React, { useState } from "react";
import "./grammarAssistantPanel.css";

const CATEGORY_DEFS = [
  {
    id: "spelling-surface",
    label: "Spelling & Surface",
    description: "Typos, repeated words, sentence-start capitalization.",
  },
  {
    id: "core-grammar",
    label: "Core Grammar & Usage",
    description: "Subject–verb agreement, tense, pronouns, articles, negatives.",
  },
  {
    id: "sentence-clarity",
    label: "Sentence Structure & Clarity",
    description: "Fragments, run-ons, misplaced modifiers, convoluted sentences.",
  },
  {
    id: "punctuation-mechanics",
    label: "Punctuation & Mechanics",
    description: "Commas, periods, semicolons, quotation punctuation.",
  },
  {
    id: "style-wordiness",
    label: "Style, Tone & Wordiness",
    description: "Redundancy, adverbs, weak verbs, repetition, formality.",
  },
  {
    id: "consistency-formatting",
    label: "Consistency & Formatting",
    description: "Hyphenation, US/UK spelling, numbers, capitalization.",
  },
];

export default function GrammarAssistantPanel({ getArticleText, onIssueClick }) {
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState(null);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);

  const handleAnalyze = async () => {
    if (!getArticleText) return;
    const articleText = getArticleText() || "";

    if (!articleText.trim()) {
      setError("There’s no text to analyze yet.");
      return;
    }

    const apiBase = (process.env.REACT_APP_BACKEND_API || "").trim();
    if (!apiBase) {
      setError("Grammar Assistant is misconfigured (missing REACT_APP_BACKEND_API).");
      return;
    }

    setLoading(true);
    setError(null);

    const url = `${apiBase.replace(/\/$/, "")}/grammar/check`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ articleText }),
        signal: controller.signal,
      });

      const contentType = res.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");

      const payload = isJson
        ? await res.json().catch(() => null)
        : await res.text().catch(() => "");

      if (!res.ok) {
        const debugId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

        const backendMsg =
          (payload && typeof payload === "object" && (payload.message || payload.error)) || "";

        const statusHint =
          res.status === 400 ? "Invalid request." :
          res.status === 401 ? "You may be signed out." :
          res.status === 403 ? "You don’t have permission." :
          res.status === 413 ? "Draft is too large." :
          res.status === 429 ? "Too many requests right now." :
          res.status >= 500 ? "Server error." :
          "Request failed.";

        setError(`${statusHint} ${backendMsg ? backendMsg : "Please try again."} (Ref: ${debugId})`);

        console.error("GrammarAssistantPanel request failed", {
          debugId,
          url,
          status: res.status,
          statusText: res.statusText,
          payload,
        });

        return;
      }

      // Guard: success responses must be JSON objects
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        const debugId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

        setError(`Unexpected response format from grammar service. (Ref: ${debugId})`);

        console.error("GrammarAssistantPanel unexpected success payload", {
          debugId,
          url,
          status: res.status,
          statusText: res.statusText,
          contentType,
          payload,
        });

        return;
      }

      setIssues(payload.issues || []);
      setLastAnalyzedAt(new Date());
    } catch (err) {
      const debugId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

      let message = "Couldn’t reach the grammar service.";
      const msg = String(err?.message || "");
      if (err?.name === "AbortError") {
        message = "Grammar request timed out (30s).";
      } else if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        message = "Network or CORS error while contacting the grammar service.";
      }

      setError(`${message} (Ref: ${debugId})`);

      console.error("GrammarAssistantPanel error", {
        debugId,
        url,
        errName: err?.name,
        errMessage: err?.message,
        errStack: err?.stack,
      });
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // Group issues by type
  const grammarIssues = issues.filter((i) => i.type === "grammar");
  const nonGrammarIssues = issues.filter((i) => i.type !== "grammar");

  // Consolidate repeated spelling issues ("thundersnow" 13 times → 1 card)
  const spellingGroupsMap = new Map();
  const otherGrammarIssues = [];

  grammarIssues.forEach((issue) => {
    if (issue.ruleId === "SPELLING") {
      // Extract the word inside quotes: Possible spelling error: “word”.
      const match =
        issue.message && issue.message.match(/“(.+?)”/);
      const key = match ? match[1].toLowerCase() : null;

      if (!key) {
        otherGrammarIssues.push(issue);
        return;
      }

      if (!spellingGroupsMap.has(key)) {
        // Keep the first issue as the base
        spellingGroupsMap.set(key, { ...issue, occurrences: 1 });
      } else {
        const existing = spellingGroupsMap.get(key);
        spellingGroupsMap.set(key, {
          ...existing,
          occurrences: (existing.occurrences || 1) + 1,
        });
      }
    } else {
      otherGrammarIssues.push(issue);
    }
  });

  const groupedGrammarIssues = [
    ...spellingGroupsMap.values(),
    ...otherGrammarIssues,
  ];

  const allDisplayIssues = [...groupedGrammarIssues, ...nonGrammarIssues];

  // After you’ve built `allDisplayIssues` (including grouped spelling issues)
  const categorizedIssues = CATEGORY_DEFS.map((cat) => ({
    ...cat,
    items: allDisplayIssues.filter((issue) => issue.category === cat.id),
  }));

  return (
    <div className="grammar-assistant-widget">
      <div className="grammar-assistant-header">
        <div>
          <h3>Grammar Assistant (Beta)</h3>
          <p className="grammar-assistant-subtitle">
            Mechanical checks only: spelling, punctuation, fillers, repetition.
          </p>
        </div>
        <button
          onClick={handleAnalyze}
          disabled={loading}
          className="grammar-assistant-button"
        >
          {loading ? "Analyzing…" : "Run checks"}
        </button>
      </div>

      <div className="grammar-assistant-meta-row">
        {lastAnalyzedAt && (
          <span className="grammar-assistant-meta">
            Last analyzed: {lastAnalyzedAt.toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && <div className="grammar-assistant-error">{error}</div>}

      <div className="grammar-assistant-section">
        <h4>Issues</h4>
        {issues.length === 0 && !loading && (
          <p className="grammar-assistant-empty">No issues flagged yet.</p>
        )}

        {/* Before first run: simple prompt only */}
        {!lastAnalyzedAt && !loading && !error && (
          <p className="grammar-assistant-empty">
            Run checks to see grammar feedback for this draft.
          </p>
        )}

        {/* After run: show results (including "no issues" state) */}
        {lastAnalyzedAt && issues.length === 0 && !loading && (
          <p className="grammar-assistant-empty">No issues flagged yet.</p>
        )}

        {lastAnalyzedAt && (
          <div className="ga-section-group">
            {categorizedIssues.map((cat) => (
              <div key={cat.id} className="ga-section">
               <div className="ga-section-header">
                  <div>
                    <h4>{cat.label}</h4>
                    {cat.description && (
                      <p className="ga-section-subtitle">{cat.description}</p>
                    )}
                  </div>
                  <span className="ga-section-count">{cat.items.length}</span>
                </div>

                {cat.items.length === 0 ? (
                  <p className="grammar-assistant-empty">
                    No issues in this category.
                  </p>
                ) : (
                  <ul className="grammar-assistant-issues">
                    {cat.items.map((issue, i) => (
                      <li
                        key={`${cat.id}-${i}`}
                        className={`ga-issue ga-issue-${issue.type}`}
                        onClick={() => onIssueClick && onIssueClick(issue)}
                      >
                        <div className="ga-issue-header">
                          <span className="ga-issue-tag">
                            {issue.type}
                          </span>
                          <span
                            className={`ga-issue-tag ga-issue-severity-${issue.severity}`}
                          >
                            {issue.severity}
                          </span>

                          {issue.ruleId === "SPELLING" && issue.occurrences > 1 && (
                            <span className="ga-issue-occurrences">
                              {issue.occurrences} occurrences
                            </span>
                          )}
                        </div>

                        <div className="ga-issue-message">
                          {issue.message}
                        </div>

                        {issue.coachPrompt && (
                         <div className="ga-issue-prompt">
                            <span className="ga-issue-prompt-label">
                              Question
                            </span>
                            <span>{issue.coachPrompt}</span>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
